/*
	This hook essentially provides a subset of the interface to an FTSocket instance
	that's suitable for component use, when we want to add, modify or remove messages
	from the Notifications panel.


	##Message Formatting

	In general, when adding or updating messages, the message stored in the FTSocket instance's
	`messages` will be updated where your provided message arguments will be stored under an `info` property
	on the parent message object.

	Conventionally, you should always include an `origin`, `task` and `status`, and any parameters that
	would be useful for rendering your message component should be under a `data` property.

	e.g. If you do this:
		```
			addMessage("Some Topic", {
				origin: "HOPI",
				task: "Validate Upload",
				status: "In Progress",
				data: {
					filename: "file.mp4"
				},
			});
		```
		You'll end up adding a message object to the list that looks like:
			{
				id: 12345,
				isRead: false,
				info: {
					// id and jobId are generated for you if you don't specify them (recommended)
					id: 12345,
					jobId: 12345,
					timestamp: 234567, // seconds (not ms) since epoch

					// Stuff you actually specified:
					origin: "HOPI",
					task: "Validate Upload",
					status: "In Progress",
					data: {
						filename: "file.mp4"
					},
				}
			}

		The content in `info` is also what you'll receive as the argument for your transform function
		when using `transformMessage`.

		If your message contains a `component` property, it will be used to render your message.
		It will also receive just the `info` portion of the message object.

		Your component will receive props that look like:

		{
			...message.info,

			// Stuff provided from socket/MessageItem -- See that file for more details on icon mapping, etc.:
			iconInfo: { // Provided for free based on the message `status`
				icon, // icon className
				title, // hover text for the icon
			},
			prettyTime, // Human readable time description, e.g. "2 minutes ago"
			clearMessage, // function that removes the message from the Notifications panel message list
		}


	##Example usage:
	```
		function ValidatingMessage(props){
			let { numFiles } = props.data || {};
			return <div>Validating {numFiles} files.</div>;
		}

		function ValidatingComponent(){
			const dispatch = useDispatch();
			const {
				addMessage,
				readMessage,
			} = useNotifications();

			async function onFilesSelected(files){
				// Add a new message that's rendered by the ValidatingMessage component
				let messageId = addMessage("ValidatingTopic", {
					origin: "Hopi",
					task: "Validate Upload",
					status: "In Progress",
					data: {
						numFiles: files.length,
					},
					component: ValidatingMessage,
				});

				try{
					await dispatch(validateFiles(files));

					// If we've reached this point, the validation succeeded
					// Mark the message as read, which removes it from the Notifications panel
					readMessage(messageId);
				} catch(e){
					// Our dispatched action threw an error indicating the validate action failed,
					// so just update the message status.
					transformMessage("ValidatingTopic", messageId, (message) => {
						return {
							...message,
							status: "Failed",
						};
					});
				}

			}
			...
		}
	```
*/

import Gravemind from "../Gravemind";

export default function useNotifications(socketController = Gravemind){
	function addMessage(topic, message){
		/*
			Adds a message to the MessageList and returns the id of the new message.
			If one isn't specified in `message.id`, one will be generated.
		*/
		return socketController.addMessage(topic, message);
	}

	function readMessage(messageId){
		// Marks a message as read and removes it from the MessageList
		return socketController.readMessage(messageId);
	}

	function updateMessage(topic, message){
		// Replaces the message with the matching message.id with the new message
		return socketController.updateMessage(topic, message);
	}

	function transformMessage(topic, messageId, transform){
		// Replaces the message with the matching message.id with the return value from `transform(message)`
		return socketController.transformMessage(topic, messageId, transform);
	}

	return {
		addMessage,
		updateMessage,
		transformMessage,
		readMessage,
	};
}
